import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Download, Star, ArrowLeft, CheckCircle2, Sparkles, CreditCard } from 'lucide-react';
import { plugins } from '@/data/pluginsData';
import NotFound from './NotFound';
import { PluginPurchaseDialog } from '@/components/wordpress/PluginPurchaseDialog';
import { PluginThankYouDialog } from '@/components/wordpress/PluginThankYouDialog';
import { PluginFreeDownloadDialog } from '@/components/wordpress/PluginFreeDownloadDialog';

const PluginDetail = () => {
  const {
    slug
  } = useParams();
  const location = useLocation();
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [freeDownloadDialogOpen, setFreeDownloadDialogOpen] = useState(false);
  const [thankYouDialogOpen, setThankYouDialogOpen] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const plugin = plugins.find(p => p.slug === slug);

  // Check for payment success and scroll to top
  useEffect(() => {
    // Always scroll to top when this page loads
    window.scrollTo(0, 0);

    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    if (sessionId && success === 'true') {
      // Payment was successful, show thank you dialog
      setThankYouDialogOpen(true);

      // Optionally get email from URL params if passed
      const email = searchParams.get('email');
      if (email) {
        setCustomerEmail(email);
      }
    }
  }, [location]);
  if (!plugin) {
    return <NotFound />;
  }
  const isPremiumPlugin = plugin.slug === 'activity-monitor-pro';
  return <>
    <Helmet>
      <title>{plugin.name} - AI WordPress Plugin | Lovabuild</title>
      <meta name="description" content={plugin.description} />
      <meta name="keywords" content={`${plugin.name}, WordPress plugin, AI, ${plugin.category}`} />
    </Helmet>

    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{
          backgroundImage: `url(${plugin.heroImage})`
        }} />
        <div className={`absolute inset-0 bg-gradient-to-br ${plugin.color} opacity-10`} />

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <Link to="/wordpress-plugins" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6 sm:mb-8 transition-colors text-sm sm:text-base" onClick={() => window.scrollTo(0, 0)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Plugins
          </Link>

          <div className="max-w-4xl">
            <Badge className="mb-3 sm:mb-4 bg-primary/10 text-primary border-primary/20 text-xs sm:text-sm">
              {plugin.category}
            </Badge>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
              {plugin.name}
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-6 sm:mb-8">
              {plugin.tagline}
            </p>

            {plugin.rating && plugin.downloads && <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 sm:w-5 sm:h-5 ${i < Math.floor(plugin.rating!) ? 'fill-yellow-400 text-yellow-400' : 'text-muted'}`} />)}
                </div>
                <span className="font-semibold text-sm sm:text-base">{plugin.rating}</span>
              </div>
              <div className="text-muted-foreground text-sm sm:text-base">
                {plugin.downloads?.toLocaleString()} downloads
              </div>
            </div>}

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              {!isPremiumPlugin && (
                <Button size="lg" className="gap-2 w-full sm:w-auto" onClick={() => setFreeDownloadDialogOpen(true)}>
                  <Download className="w-5 h-5" />
                  Download Free Version
                </Button>
              )}
              {isPremiumPlugin && (
                <Button size="lg" variant="default" onClick={() => setPurchaseDialogOpen(true)} className="gap-2 w-full sm:w-auto">
                  <CreditCard className="w-5 h-5" />
                  Get Premium Enterprise
                </Button>
              )}
            </div>

            {plugin.pricing && <div className="mt-4 sm:mt-6">

              {plugin.pricing.license && <p className="text-xs sm:text-sm text-muted-foreground">{plugin.pricing.license}</p>}
            </div>}
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="py-8 sm:py-12 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-center">Watch It In Action</h2>
            <div className="aspect-video rounded-lg sm:rounded-xl overflow-hidden bg-black shadow-2xl">
              <iframe width="100%" height="100%" src={plugin.demoVideoUrl} title={`${plugin.name} Demo`} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-10 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              {plugin.description}
            </p>
          </div>
        </div>
      </section>

      {/* Features & Benefits */}
      <section className="py-10 sm:py-16 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 sm:gap-12">
            {/* Features */}
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                <h2 className="text-2xl sm:text-3xl font-bold">Key Features</h2>
              </div>
              <ul className="space-y-2 sm:space-y-3">
                {plugin.features.map((feature, index) => <li key={index} className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm sm:text-base text-muted-foreground">{feature}</span>
                </li>)}
              </ul>
            </div>

            {/* Benefits */}
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                <h2 className="text-2xl sm:text-3xl font-bold">Benefits</h2>
              </div>
              <ul className="space-y-2 sm:space-y-3">
                {plugin.benefits.map((benefit, index) => <li key={index} className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-sm sm:text-base text-muted-foreground">{benefit}</span>
                </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-10 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Perfect For</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {plugin.useCases.map((useCase, index) => <Card key={index} className="p-4 sm:p-6 bg-card border-border hover:border-primary/50 transition-colors">
                <p className="font-semibold text-center text-sm sm:text-base">{useCase}</p>
              </Card>)}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {plugin.gallery.length > 0 && <section className="py-10 sm:py-16 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Screenshots</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {plugin.gallery.map((image, index) => <div key={index} className="aspect-video rounded-lg overflow-hidden shadow-lg">
              <img src={image} alt={`${plugin.name} screenshot ${index + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>)}
          </div>
        </div>
      </section>}

      {/* Technologies */}
      <section className="py-10 sm:py-16">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Built With</h2>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
              {plugin.technologies.map((tech, index) => <Badge key={index} variant="outline" className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm">
                {tech}
              </Badge>)}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {plugin.testimonial && <section className="py-10 sm:py-16 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="p-5 sm:p-8 md:p-12 bg-card border-border">
              <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-6">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />)}
              </div>
              <blockquote className="text-lg sm:text-xl md:text-2xl mb-4 sm:mb-6 italic">
                "{plugin.testimonial.quote}"
              </blockquote>
              <div>
                <p className="font-semibold text-base sm:text-lg">{plugin.testimonial.author}</p>
                <p className="text-sm sm:text-base text-muted-foreground">
                  {plugin.testimonial.position}, {plugin.testimonial.company}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>}

      {/* CTA Section */}
      <section className="py-12 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
              Ready to Transform Your WordPress Site?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8">
              {isPremiumPlugin
                ? `Get ${plugin.name} Premium Enterprise today.`
                : `Download ${plugin.name} today or request a custom version tailored to your needs.`
              }
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
              {isPremiumPlugin ? (
                <Button size="lg" className="gap-2 w-full sm:w-auto" onClick={() => setPurchaseDialogOpen(true)}>
                  <CreditCard className="w-5 h-5" />
                  Get Premium Enterprise
                </Button>
              ) : (
                <>
                  <Button size="lg" className="gap-2 w-full sm:w-auto">
                    <Download className="w-5 h-5" />
                    Download Now
                  </Button>
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    Request Custom Plugin
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>

    {/* Dialogs */}
    <PluginFreeDownloadDialog open={freeDownloadDialogOpen} onOpenChange={setFreeDownloadDialogOpen} pluginName={plugin.name} downloadUrl="/downloads/activity-monitor-pro_free.zip" />

    {isPremiumPlugin && <>
      <PluginPurchaseDialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen} pluginName={plugin.name} pluginSlug={plugin.slug} />
      <PluginThankYouDialog open={thankYouDialogOpen} onOpenChange={setThankYouDialogOpen} pluginName={plugin.name} customerEmail={customerEmail} />
    </>}
  </>;
};
export default PluginDetail;